import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, TextField, InputAdornment, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SchedulingFormData } from '../hooks/useSchedulingFormSchema';
import { ReminderConfigurationForm } from './ReminderConfigurationForm';

type SchedulingConfigurationFormProps = {
  control: Control<SchedulingFormData>;
};

export const SchedulingConfigurationForm: React.FC<
  SchedulingConfigurationFormProps
> = ({ control }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ mt: 3 }}>
      {/* Top row - Duration and Validity Configuration */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {/* Notification Days Configuration Box */}
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            flex: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t(
              'pages.questionnaireScheduling.configuration.numberOfDays.helper',
            )}
          </Typography>
          <Controller
            name="configuration.numberOfDays"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type="number"
                label={t(
                  'pages.questionnaireScheduling.configuration.numberOfDays.label',
                )}
                error={!!error}
                helperText={error?.message || ''}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t(
                          'pages.questionnaireScheduling.configuration.numberOfDays.unit',
                        )}
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 1,
                  },
                }}
                sx={{ maxWidth: 300 }}
                required
              />
            )}
          />
        </Box>

        {/* Questionnaire Validity Configuration Box */}
        <Box
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            flex: 1,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t(
              'pages.questionnaireScheduling.configuration.validityInMinutes.helper',
            )}
          </Typography>
          <Controller
            name="configuration.validityInMinutes"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                type="number"
                label={t(
                  'pages.questionnaireScheduling.configuration.validityInMinutes.label',
                )}
                error={!!error}
                helperText={error?.message || ''}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {t(
                          'pages.questionnaireScheduling.configuration.validityInMinutes.unit',
                        )}
                      </InputAdornment>
                    ),
                  },
                  htmlInput: {
                    min: 1,
                  },
                }}
                sx={{ maxWidth: 300 }}
                required
              />
            )}
          />
        </Box>
      </Box>

      {/* Bottom row - Reminder Configuration */}
      <ReminderConfigurationForm control={control} />
    </Box>
  );
};
